/*
 * The Gulmohar Co. — canonical global cart drawer.
 *
 * Backs snippets/cart-drawer-tgc.liquid (rendered once in layout/theme.liquid
 * and once in layout/theme.pdp.liquid). Any trigger element anywhere in the
 * theme can open this drawer by carrying a `data-cart-drawer-trigger`
 * attribute — no per-page wiring needed.
 *
 * Also listens for the theme's global 'cart:update' event (dispatched as
 * CartAddEvent/CartUpdateEvent by assets/product-form.js from product cards,
 * Quick View, and anywhere else in the theme) so an add-to-cart anywhere
 * refreshes and opens this same drawer instead of any other cart UI.
 */
(() => {
  "use strict";

  const cartDrawer = document.getElementById("pdpCartDrawer");
  const cartBackdrop = document.getElementById("pdpCartBackdrop");
  const cartClose = document.getElementById("pdpCartClose");
  const cartItemsEl = document.getElementById("pdpCartItems");
  const cartTotalEl = document.getElementById("pdpCartTotal");
  const cartHeadCountEl = document.getElementById("pdpCartHeadCount");
  const cartCheckoutEl = document.getElementById("pdpCartCheckout");

  if (!cartDrawer || !cartBackdrop) return;

  const autoOpen = cartDrawer.dataset.autoOpen === "true";
  const currency = cartDrawer.dataset.currency || "INR";
  /** Shopify's shop.money_format template, e.g. "Rs. {{amount_with_comma_separator}}" — matches the server-rendered `| money` output exactly. */
  const moneyFormatTemplate = cartDrawer.dataset.moneyFormat || "{{amount}}";

  /** Replicates Shopify's `money` liquid filter client-side, given cents. */
  function moneyFormat(cents) {
    return moneyFormatTemplate.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      let thousandsSeparator = ",";
      let decimalSeparator = ".";
      let precision = 2;

      switch (placeholder) {
        case "amount":
          break;
        case "amount_no_decimals":
          precision = 0;
          break;
        case "amount_with_comma_separator":
          thousandsSeparator = ".";
          decimalSeparator = ",";
          break;
        case "amount_no_decimals_with_comma_separator":
          thousandsSeparator = ".";
          precision = 0;
          break;
        case "amount_with_apostrophe_separator":
          thousandsSeparator = "'";
          break;
        case "currency":
          return currency;
        default:
          break;
      }

      const rounded = (cents / 100).toFixed(precision);
      let [whole, fraction] = rounded.split(".");
      whole = whole.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);
      return precision <= 0 ? whole : `${whole}${decimalSeparator}${(fraction || "").padEnd(precision, "0")}`;
    });
  }

  const bagCountEls = [document.getElementById("pdpBagCount"), cartHeadCountEl].filter(Boolean);

  const openBag = () => {
    cartBackdrop.hidden = false;
    cartDrawer.classList.add("is-open");
    cartDrawer.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";
  };

  const closeBag = () => {
    cartDrawer.classList.remove("is-open");
    cartDrawer.setAttribute("aria-hidden", "true");
    cartBackdrop.hidden = true;
    document.body.style.overflow = "";
  };

  document.addEventListener("click", (e) => {
    if (e.target.closest("[data-cart-drawer-trigger]")) {
      e.preventDefault();
      openBag();
    }
  });

  if (cartClose) cartClose.addEventListener("click", closeBag);
  if (cartBackdrop) cartBackdrop.addEventListener("click", closeBag);

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && cartDrawer.classList.contains("is-open")) {
      closeBag();
    }
  });

  /** Renders cart line items + total + counts from a Shopify /cart.js payload. */
  function renderCart(cart) {
    bagCountEls.forEach((el) => {
      el.textContent = cart.item_count;
    });

    if (cartTotalEl) cartTotalEl.textContent = moneyFormat(cart.total_price);

    if (cartCheckoutEl) cartCheckoutEl.disabled = cart.item_count === 0;

    if (!cartItemsEl) return;

    if (cart.item_count === 0) {
      cartItemsEl.innerHTML = '<p class="pdp-cart-empty">Your bag is empty.</p>';
      return;
    }

    cartItemsEl.innerHTML = cart.items
      .map((item, index) => {
        const line = index + 1;
        const imageStyle = item.image
          ? `background-image:url('${item.image}'); background-size:cover; background-position:center;`
          : "background:#C4A67A;";
        const variantBit = item.product_has_only_default_variant
          ? ""
          : `${item.variant_title} &middot; `;

        return `
          <div class="pdp-cart-item" data-line="${line}">
            <div class="pdp-cart-item-img" style="${imageStyle}"><div class="pdp-art-glow-sm"></div></div>
            <div>
              <div class="pdp-cart-item-cat">${item.product_type}</div>
              <div class="pdp-cart-item-name">${item.product_title}</div>
              <div class="pdp-cart-item-meta">${variantBit}Qty ${item.quantity}</div>
              <div class="pdp-cart-item-qty">
                <button class="pdp-cart-item-qty-btn" type="button" data-line="${line}" data-action="decrease" aria-label="Decrease quantity">&minus;</button>
                <span class="pdp-cart-item-qty-val" data-line="${line}">${item.quantity}</span>
                <button class="pdp-cart-item-qty-btn" type="button" data-line="${line}" data-action="increase" aria-label="Increase quantity">&plus;</button>
              </div>
            </div>
            <div class="pdp-cart-item-right">
              <div class="pdp-cart-item-price">${moneyFormat(item.final_line_price)}</div>
              <button class="pdp-cart-item-remove" type="button" data-line="${line}">Remove</button>
            </div>
          </div>
        `;
      })
      .join("");
  }

  function refreshCart() {
    return fetch("/cart.js")
      .then((res) => res.json())
      .then((cart) => {
        renderCart(cart);
        return cart;
      });
  }

  function changeLine(line, quantity) {
    return fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ line: Number(line), quantity }),
    })
      .then((res) => res.json())
      .then((cart) => {
        renderCart(cart);
        document.dispatchEvent(
          new CustomEvent("cart:update", { bubbles: true, detail: { data: { itemCount: cart.item_count, source: "cart-drawer-tgc" } } })
        );
        return cart;
      });
  }

  if (cartItemsEl) {
    cartItemsEl.addEventListener("click", (e) => {
      const removeBtn = e.target.closest(".pdp-cart-item-remove");
      const qtyBtn = e.target.closest(".pdp-cart-item-qty-btn");

      if (removeBtn) {
        const line = removeBtn.dataset.line;
        if (!line) return;
        removeBtn.disabled = true;
        changeLine(line, 0).catch(() => {
          removeBtn.disabled = false;
        });
        return;
      }

      if (qtyBtn) {
        const line = qtyBtn.dataset.line;
        const valEl = cartItemsEl.querySelector(`.pdp-cart-item-qty-val[data-line="${line}"]`);
        if (!line || !valEl) return;

        let qty = parseInt(valEl.textContent, 10) || 1;
        qty += qtyBtn.dataset.action === "increase" ? 1 : -1;
        if (qty < 0) qty = 0;

        qtyBtn.disabled = true;
        changeLine(line, qty).finally(() => {
          qtyBtn.disabled = false;
        });
      }
    });
  }

  /* Any add-to-cart elsewhere in the theme (product cards, Quick View, and
     this drawer's own AJAX changes above) dispatches this same event. */
  document.addEventListener("cart:update", (e) => {
    if (e.detail?.data?.source === "cart-drawer-tgc") return; // already rendered above
    if (e.detail?.data?.didError) return;

    refreshCart().then(() => {
      if (autoOpen) openBag();
    });
  });
})();
